(() => {
  const TICKER_SELECTOR = '.ticker';
  const TRACK_SELECTOR = '.ticker__track';
  const GROUP_SELECTOR = '.ticker__group';
  const CLONE_ATTRIBUTE = 'data-ticker-clone';
  const ITEM_CLONE_ATTRIBUTE = 'data-ticker-item-clone';
  const DEFAULT_SPEED = 80;
  const MAX_CLONES = 20;
  const MAX_ITEM_CLONES = 60;
  const animations = new WeakMap();

  const removeShopifyEditorAttributes = (element) => {
    [element, ...element.querySelectorAll('*')].forEach((node) => {
      [...node.attributes].forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify')) {
          node.removeAttribute(attribute.name);
        }
      });
    });
  };

  const setupTicker = (ticker) => {
    const viewport = ticker.querySelector('.ticker__viewport');
    const track = ticker.querySelector(TRACK_SELECTOR);
    const sourceGroup = track?.querySelector(`${GROUP_SELECTOR}:not([${CLONE_ATTRIBUTE}])`);

    if (!viewport || !track || !sourceGroup || sourceGroup.children.length === 0) {
      return;
    }

    const previousAnimation = animations.get(ticker);
    if (previousAnimation) {
      window.cancelAnimationFrame(previousAnimation);
      animations.delete(ticker);
    }

    track.dataset.tickerReady = 'false';
    track.querySelectorAll(`[${CLONE_ATTRIBUTE}]`).forEach((clone) => clone.remove());
    sourceGroup.querySelectorAll(`[${ITEM_CLONE_ATTRIBUTE}]`).forEach((clone) => clone.remove());
    track.style.transform = 'translate3d(0, 0, 0)';

    const sourceItems = [...sourceGroup.children];
    let sourceWidth = sourceGroup.getBoundingClientRect().width;
    const viewportWidth = viewport.getBoundingClientRect().width;
    let itemCloneCount = 0;

    while ((sourceWidth < viewportWidth * 2 || itemCloneCount === 0) && itemCloneCount < MAX_ITEM_CLONES) {
      sourceItems.forEach((item) => {
        const clone = item.cloneNode(true);

        clone.setAttribute(ITEM_CLONE_ATTRIBUTE, '');
        clone.setAttribute('aria-hidden', 'true');
        removeShopifyEditorAttributes(clone);
        sourceGroup.appendChild(clone);
        itemCloneCount += 1;
      });

      sourceWidth = sourceGroup.getBoundingClientRect().width;
    }

    const firstItem = sourceItems[0];
    const firstItemClone = sourceGroup.querySelector(`[${ITEM_CLONE_ATTRIBUTE}]`);
    const loopDistance = firstItem && firstItemClone ? firstItemClone.offsetLeft - firstItem.offsetLeft : sourceWidth;

    if (sourceWidth <= 0 || viewportWidth <= 0 || loopDistance <= 0) {
      return;
    }

    let trackWidth = sourceWidth;
    let cloneCount = 0;
    const minimumTrackWidth = viewportWidth + sourceWidth * 2;

    while (trackWidth < minimumTrackWidth && cloneCount < MAX_CLONES) {
      const clone = sourceGroup.cloneNode(true);

      clone.setAttribute(CLONE_ATTRIBUTE, '');
      clone.setAttribute('aria-hidden', 'true');
      removeShopifyEditorAttributes(clone);
      track.appendChild(clone);

      trackWidth += clone.getBoundingClientRect().width;
      cloneCount += 1;
    }

    const speed = Number(ticker.dataset.tickerSpeed) || DEFAULT_SPEED;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.style.setProperty('--ticker-distance', `${loopDistance}px`);
    track.dataset.tickerReady = 'true';

    if (prefersReducedMotion) {
      return;
    }

    let offset = 0;
    let lastTimestamp;

    const tick = (timestamp) => {
      if (lastTimestamp === undefined) {
        lastTimestamp = timestamp;
      }

      const elapsed = timestamp - lastTimestamp;
      lastTimestamp = timestamp;
      offset = (offset + (elapsed * speed) / 1000) % loopDistance;
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;

      animations.set(ticker, window.requestAnimationFrame(tick));
    };

    animations.set(ticker, window.requestAnimationFrame(tick));
  };

  const setupTickers = (root = document) => {
    root.querySelectorAll(TICKER_SELECTOR).forEach(setupTicker);
  };

  const scheduleSetup = () => {
    window.requestAnimationFrame(() => setupTickers());
  };

  document.addEventListener('DOMContentLoaded', scheduleSetup);
  document.addEventListener('shopify:section:load', (event) => setupTickers(event.target));
  window.addEventListener('resize', scheduleSetup);

  if (document.fonts?.ready) {
    document.fonts.ready.then(scheduleSetup);
  }
})();
